import { User } from './state/user/UserTypes';
import { PartialReducer, FluxStandardAction } from './state/StateTypes';
import { SubscriptionTiers, Subscription } from 'state/subscription/SubscriptionTypes';
import compareAsc from 'date-fns/compareAsc';
import ReactModal from 'react-modal';
import { ErrorCodes } from 'types';

export const nowInUnixEpochTime = () => {
   // Time in UNIX Epoch, with decimals rounded up
   return Math.round(new Date().getTime() / 1000);
};

export const convertTimeStamp = (stamp: number | string): string => {
   //designed to handle converting both Unix time stamps (number) and ISO dates (strings)
   const isISODate = typeof stamp === 'string';
   const date = isISODate ? new Date(stamp) : new Date((stamp as number) * 1000);
   return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

export const createReducer = <T, A extends FluxStandardAction>(initialState: T, handlers: { [type: string]: PartialReducer<T, A> }) => {
   return (state: T = initialState, action: A): T => {
      const handler = handlers[action && action.type];
      if (!handler) {
         return state;
      }
      return handler(state, action);
   };
};

export const buildQueryString = (props: Object): string => {
   const query: string[] = [];
   for (const key in props) {
      if (!!props[key]) {
         query.push(`${key}=${encodeURIComponent(props[key])}`);
      }
   }
   return query.join('&');
};

export const getParamFromUrl = (url: string, param: string | string[]): string | undefined => {
   const formattedParams: string = Array.isArray(param) ? param.join() : param;
   const results = new RegExp('[?&]' + formattedParams + '=([^&#]*)', 'ig').exec(url);
   if (!results) return;

   return decodeURI(results[1]);
};

const isValidByte = (byte: string): boolean => {
   const min: number = 0;
   const max: number = 255;
   const number = parseInt(byte) || parseInt(byte, 16);
   return number >= min && number <= max;
};

export const validateBytes = (bytes: string, length: number): number[] | null => {
   let formattedBytes: number[] | null = [];
   bytes.split(/,\s?/).forEach((byte: string) => {
      if (isValidByte(byte)) {
         formattedBytes && formattedBytes.push(parseInt(byte) || parseInt(byte, 16));
      } else {
         formattedBytes = null;
         return formattedBytes;
      }
   });

   return formattedBytes.length === length ? formattedBytes : null;
};

export const validateEmail = (email: string): boolean => {
   const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
   return regex.test(email.toLocaleLowerCase());
};

export const determineUserTier = (user: User): SubscriptionTiers => {
   const paidSub: Subscription | undefined = user.subscription.find(sub => sub.tier === SubscriptionTiers.Paid);
   if (paidSub && !dateIsAfterToday(paidSub.expirationDate)) {
      return SubscriptionTiers.Paid;
   } else {
      return SubscriptionTiers.Free;
   }
};

export const deriveDateFromMongoId = (id: string): string => {
   const timeStamp: Date = new Date(parseInt(id.slice(0, 8), 16) * 1000);
   return convertTimeStamp(String(timeStamp));
};

export const truncate = (str: string, maxLength: number): string => {
   return `${str.substring(0, maxLength)}...`;
};

export const toPascalCase = (str: string): string => {
   return str.toLocaleLowerCase().replace(/\w\S*/g, string => string.charAt(0).toLocaleUpperCase() + string.substr(1).toLocaleLowerCase());
};

export const scrollPageTo = (position: 'top' | 'bottom') => {
   window.scrollTo({ top: position === 'bottom' ? document.body.scrollHeight : 0, left: 0, behavior: 'smooth' });
};

export const pluralize = (str: string) => (str.endsWith('s') ? str : str + 's');

export const getModalPortalContainer = (() => {
   let portalContainer: HTMLDivElement | null = null;
   return (): HTMLDivElement => {
      // Check if singleton exists, if so, return
      if (!!portalContainer) {
         return portalContainer;
      }
      // Initialize singleton and save to holder variable
      portalContainer = document.createElement('div');
      portalContainer.className = 'modal-content';
      document.querySelector('body')?.prepend(portalContainer);

      // Initialize React Modal setup for container
      ReactModal.setAppElement(portalContainer);

      return portalContainer;
   };
})();

export const dateIsAfterToday = (date: string | number | Date): boolean => compareAsc(new Date(date), new Date()) !== 1;

export const presentErrorBasedOnCode = (code: ErrorCodes): string => {
   switch (code) {
      case ErrorCodes.DATA_VALIDATION_ERROR:
         return "There was an issue validating your request. Please check to make sure you've entered all fields correctly";
      case ErrorCodes.INVALID_CREDENTIALS:
         return 'The provided credentials are invalid. Please try again';
      case ErrorCodes.NEW_PASSWORD_REQUIRED:
         return 'Please set your new password';
      case ErrorCodes.NO_RESULTS_ERROR:
         return "We weren't able to find the results you are looking for. Please check your internet connection and try again";
      case ErrorCodes.UNAUTHORIZED:
         return 'You are not authorized to view this resource';
      case ErrorCodes.BAD_USERNAME:
         return 'The username you provided does not match a username in our system';
      case ErrorCodes.CODE_MISMATCH:
         return 'The code provided does not match the required code. Please check your email to be sure you are using the right code';
      case ErrorCodes.DATABASE_ERROR:
      case ErrorCodes.INTERNAL_ERROR:
         return 'An unknown error occurred. We apologize for the inconvenience';
      case ErrorCodes.DUPLICATE_ERROR:
         return 'We already have a piece of identical data in our system';
      case ErrorCodes.EXPIRED_CODE:
         return 'Your password reset code has expired.';
      default:
         return 'An unknown error occurred. Please check your internet connection and try again';
   }
};
